import { useQueryClient } from '@tanstack/react-query';
import { type APIError, queryKeys, useDeleteImageFromCollectionMutation } from '@vizit/api';
import * as React from 'react';

interface UseRemoveAssetFromCollectionProps {
  /**
   * The portfolio id
   */
  portfolioId: string;
  /**
   * The collection id
   */
  collectionId: string;
  /**
   * The function to call when an authentication error occurs
   */
  onAuthenticationError?: (error: unknown | APIError) => void;
}

/**
 * A hook to remove an asset from a collection
 * @param props - The props for the hook
 * @returns The hook
 */
function useRemoveAssetFromCollection(props: UseRemoveAssetFromCollectionProps) {
  const { portfolioId, collectionId, onAuthenticationError } = props;

  const [isOpen, setIsOpen] = React.useState(false);
  const [imageIdToRemove, setImageIdToRemove] = React.useState<string | null>(null);

  const queryClient = useQueryClient();

  const { mutateAsync } = useDeleteImageFromCollectionMutation();

  const handleOpenChange = (isOpen: boolean) => {
    setIsOpen(isOpen);
  };

  const handleRemoveImageDialogOpen = React.useCallback((id: string) => {
    setImageIdToRemove(id);
    setIsOpen(true);
  }, []);

  const handleRemove = React.useCallback(
    async (id: string) => {
      try {
        await mutateAsync({
          portfolio_id: portfolioId,
          collection_id: collectionId,
          image_id: id,
        });

        // Invalidate collection images query to refetch the updated list
        await queryClient.invalidateQueries({
          queryKey: queryKeys.collections.collectionImages({ portfolio_id: portfolioId, collection_id: collectionId })
            .queryKey,
        });

        // Also invalidate the collection detail to update the count
        await queryClient.invalidateQueries({
          queryKey: queryKeys.collections.detail({ portfolio_id: portfolioId, collection_id: collectionId }).queryKey,
        });

        // Invalidate Asset Library queries since the image is permanently deleted
        await queryClient.invalidateQueries({
          queryKey: queryKeys.images.infinite({ portfolio_id: portfolioId }).queryKey,
        });

        // Invalidate the specific image detail if it's cached
        await queryClient.invalidateQueries({
          predicate: (query) => {
            const key = query.queryKey;
            if (Array.isArray(key) && key[0] === 'GetImage' && key.length >= 2 && typeof key[1] === 'object') {
              const vars = key[1] as Record<string, unknown>;
              return vars.portfolio_id === portfolioId && vars.image_id === id;
            }
            return false;
          },
        });
      } catch (error) {
        onAuthenticationError?.(error);
      }

      setImageIdToRemove(null);
      setIsOpen(false);
    },
    [portfolioId, collectionId, queryClient, mutateAsync, onAuthenticationError],
  );

  return {
    isOpen,
    imageIdToRemove,
    onOpenChange: handleOpenChange,
    onRemoveImageDialogOpen: handleRemoveImageDialogOpen,
    onRemove: handleRemove,
  };
}

export { useRemoveAssetFromCollection };
